(ns arthur.ui.icon
  "The handful of glyphs the chrome uses, drawn here rather than imported.

  NOT an icon library, on purpose. Three reasons, in order of weight:

  1. SIZE. These render at 11px against 11px type. Lucide, Feather and the rest
     are drawn on a 24px grid with a 2px stroke and sub-pixel curves; scaled to
     11 they go soft and land off the pixel grid, in a tool whose entire premise
     is that you can see individual pixels. These are drawn on a 12px grid with
     whole and half coordinates, so the bars and edges fall where the renderer
     wants them.
  2. REGISTER. `app.css` argues the chrome is Macromedia-era — flat, hairline,
     no bevels, one accent. A modern rounded icon set is a second visual voice
     in a strip 21px tall.
  3. COST. Six glyphs is forty lines. The alternative is a dependency, a build
     step's worth of tree-shaking, and a license file, for forty lines.

  Icons are used ONLY where the word is worse than the picture: the transport,
  where `|<` was ASCII pretending to be a glyph, the two playback toggles, which
  are state rather than actions, and a lane's row in the timeline, where the
  picture is read while scanning a column of twenty names and the word would
  not be. Everything else in the strip keeps its word, because `insert` and
  `overwrite` have no pictures and inventing some would be a puzzle rather than
  a shorthand. Every one carries an `aria-label` or a text label beside it at
  the call site; nothing here is the only statement of what a control does."
  (:require [clojure.string :as str]))

;; A glyph is its paths on a 12x12 grid. Solid shapes are filled; the two
;; drawn with a line are stroked, which `kind` says.
(def ^:private glyphs
  {:start {:fill ["M2 2.5h1.3v7H2z"          ; the bar it stops against
                  "M10 2.5v7L4.2 6z"]}       ; and the triangle into it
   :end   {:fill ["M8.7 2.5H10v7H8.7z" "M2 2.5v7L7.8 6z"]}
   :play  {:fill ["M3.4 2.3L10 6 3.4 9.7z"]}
   :pause {:fill ["M3.3 2.4h1.9v7.2H3.3z"
                  "M6.8 2.4h1.9v7.2H6.8z"]}
   ;; Two arrows round a rectangle: the clip's end rejoining its start.
   :loop  {:stroke ["M3 5.4V4.6A1.2 1.2 0 0 1 4.2 3.4h5"
                    "M7.8 1.8 9.4 3.4 7.8 5"
                    "M9 6.6v.8A1.2 1.2 0 0 1 7.8 8.6h-5"
                    "M4.2 7 2.6 8.6 4.2 10.2"]}
   ;; One speaker, with or without the sound coming out of it.
   :sound {:fill   ["M2.4 4.6h1.8L6.4 2.6v6.8L4.2 7.4H2.4z"]
           :stroke ["M8 4.1a2.7 2.7 0 0 1 0 3.8"]}
   :muted {:fill   ["M2.4 4.6h1.8L6.4 2.6v6.8L4.2 7.4H2.4z"]
           :stroke ["M7.9 4.4 10.7 7.6" "M10.7 4.4 7.9 7.6"]}
   ;; A nested timeline: the larger frame is the symbol and the inset frame is
   ;; the content it owns.
   :symbol {:stroke ["M1.5 2.5h9v7h-9z" "M3.5 4.5h5v3h-5z"]}
   ;; A strip divided into cels: what a lane IS, drawn rather than named, so one
   ;; row out of a dozen says at a glance that its blocks follow one another in
   ;; time instead of being on screen together. Whole and half coordinates, so
   ;; the three dividers land on pixels at 11px.
   :lane  {:stroke ["M1.5 3.5h9v5h-9z" "M4.5 3.5v5" "M7.5 3.5v5"]}
   ;; To the key before and after: the transport's step, without its bar.
   :prev  {:fill ["M8.5 3v6L4 6z"]}
   :next  {:fill ["M3.5 3v6L8 6z"]}
   ;; The gap between two keys as a value graph draws it: a ramp from one to
   ;; the other, or a step that holds and then jumps.
   :tween {:fill ["M1.5 8h2v2h-2z" "M8.5 2h2v2h-2z"] :stroke ["M3 8.5 9 3.5"]}
   :hold  {:fill ["M1.5 8h2v2h-2z" "M8.5 2h2v2h-2z"] :stroke ["M3.5 9H9.5V4"]}
   ;; How an instance plays: through once into a stop, round, or one frame.
   :once  {:fill ["M2.4 2.6 7.6 6 2.4 9.4z" "M8.4 2.6h1.4v6.8H8.4z"]}
   :still {:stroke ["M3.5 2.5h5v7h-5z"] :fill ["M5 4.5h2v3H5z"]}
   ;; A new drawing in a lane: a sheet, with a line on it — the one at the
   ;; playhead carried on — or bare.
   :drawing {:stroke ["M3 1.5h4.5l2 2v7h-6.5z" "M4.6 7.8 5.9 6.1 7.4 7.3"]}
   :blank   {:stroke ["M3 1.5h4.5l2 2v7h-6.5z"]}
   ;; The clipboard, as every editor draws it: two sheets, scissors, a board,
   ;; and two sheets with one more.
   :copy      {:stroke ["M4.5 4.5h5v5h-5z" "M2.5 7.5v-5h5"]}
   :cut       {:stroke ["M2 9a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0"
                        "M7 9a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0"
                        "M4.4 7.8 8.5 2" "M7.6 7.8 3.5 2"]}
   :paste     {:stroke ["M3.8 2.5H2.5v8h7v-8H8.2" "M4.5 1.5h3v2h-3z"]}
   :duplicate {:stroke ["M4.5 4.5h5v5h-5z" "M2.5 7.5v-5h5" "M7 5.8v2.4" "M5.8 7h2.4"]}
   ;; Whether others may follow you: an eye, open to being watched.
   :eye {:stroke ["M1.5 6C2.8 3.8 4.3 2.8 6 2.8S9.2 3.8 10.5 6C9.2 8.2 7.7 9.2 6 9.2S2.8 8.2 1.5 6z"]
         :fill ["M4.8 6a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0 -2.4 0z"]}})

(defn view
  "The glyph named `k`, sized by CSS and inked in `currentColor` — so a button's
  `.on` state colours its icon without this knowing the palette exists."
  [k]
  (let [{:keys [fill stroke]} (glyphs k)]
    [:svg {:viewBox "0 0 12 12" :aria-hidden true :focusable false}
     (when (seq fill)
       [:path {:d (str/join " " fill) :fill "currentColor"}])
     (when (seq stroke)
       [:path {:d (str/join " " stroke) :fill "none" :stroke "currentColor"
               :stroke-width 1.2 :stroke-linecap "round" :stroke-linejoin "round"}])]))
